import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { useAdminMe } from '../components/RequireAdmin'
import { ApiError } from '../lib/api'
import { adminApi, can } from '../lib/adminApi'

/**
 * 시민문제 제목·요약 확정.
 *
 * **여기서 하는 일은 공개 화면에 나갈 제목·요약·키워드를 정하는 것뿐이다.** 구조화 축(분야·대상·자원)은
 * 검수 화면의 전환·AI 재실행이 다루고, 이 화면은 시민 원문을 그대로 노출할 수 없다는 제약
 * (017 — 원문은 신원 단서다) 아래 **비식별 요약을 관리자가 확정하는 자리**다.
 *
 * 요약은 **AI가 기본 입력하고 관리자가 덮어쓴다**(08-31 사용자 결정). 등록·수정 시 AI가 낸
 * 초안이 이미 들어와 있으면 그 문장이 보이고, 관리자가 고쳐 저장하면 그 값이 정본이 된다.
 * 비우고 저장하면 "미확정"(null)으로 되돌아가고, 그 상태에서 AI 재실행이 다시 초안을 넣는다.
 *
 * 키워드도 같은 규칙이다(041) — AI가 원문에서 뽑은 낱말이 들어와 있고, 관리자가 고쳐 저장하면
 * 그 값이 공개 상세에 그대로 나간다. 비우고 저장하면 AI 재실행이 다시 채운다.
 *
 * **검수 상태는 여기서 안 바꾼다** — 공개 전환은 검수 화면(`/admin`)이 한다.
 * 원문(rawText)은 `pii.read` 권한이 있을 때만 서버가 실어 준다(없으면 키 자체가 없다).
 */

function errorMessage(error: unknown): string | null {
  if (error === null || error === undefined) return null
  if (error instanceof ApiError) {
    if (error.status === 403) return '이 작업을 할 권한이 없습니다.'
    return error.detail.length > 0 ? error.detail.join(' / ') : error.code
  }
  return '처리 중 오류가 발생했습니다.'
}

const SUMMARY_MAX = 300 // 서버 PatchProblemSchema와 같은 값 — 화면이 먼저 막는다.
const KEYWORD_MAX = 5 // 서버 `KEYWORD_MAX`와 같은 값. 넘겨 보내면 400이라 화면이 먼저 자른다.
const KEYWORD_LENGTH_MAX = 20 // 낱말 하나의 상한(서버 스키마와 같은 값).

/** 쉼표로 나눠 낱말 목록을 만든다 — 앞머리 `#`는 장식이라 값에서 뗀다(서버 정규화와 같은 규칙). */
function parseKeywords(text: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of text.split(',')) {
    const word = raw.replace(/^#+\s*/, '').trim().slice(0, KEYWORD_LENGTH_MAX)
    if (word === '' || seen.has(word.toLowerCase())) continue
    seen.add(word.toLowerCase())
    out.push(word)
  }
  return out.slice(0, KEYWORD_MAX)
}

export function AdminProblemEditPage() {
  const { id = '' } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const me = useAdminMe()

  const card = useQuery({
    queryKey: ['admin-problem-card', id],
    queryFn: () => adminApi.problemCard(id),
  })

  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [keywordText, setKeywordText] = useState('')

  // 저장하지 않은 편집 내용을 서버 응답으로 덮어쓰지 않으려고, 폼 초기화는 한 번만 한다.
  const loadedId = useRef<string | null>(null)

  useEffect(() => {
    const c = card.data
    if (c === undefined || loadedId.current === c.id) return
    loadedId.current = c.id
    setTitle(c.title)
    setSummary(c.summary ?? '')
    setKeywordText(c.keywords.join(', '))
  }, [card.data])

  const mayWrite = can(me.data, 'card', 'write')

  const save = useMutation({
    mutationFn: () => {
      const t = title.trim()
      const s = summary.trim()
      // 요약을 비우면 null — 빈 문자열을 저장하면 "미확정"과 "빈 값"이 섞인다(서버도 min(1)).
      return adminApi.updateProblemCard(id, {
        title: t,
        summary: s === '' ? null : s,
        keywords: parseKeywords(keywordText),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-problem-card', id] })
      void queryClient.invalidateQueries({ queryKey: ['admin-cards', 'problem'] })
    },
  })

  const c = card.data
  const canSave = mayWrite && title.trim() !== '' && !save.isPending
  const actionError = errorMessage(save.error)
  // 원문은 권한이 있을 때만 온다 — 키 자체의 유무로 "권한 없음"과 "원문 없음"을 가른다.
  const canReadRaw = c !== undefined && 'rawText' in c
  // 저장할 값과 미리보기가 같은 함수를 지난다 — 화면이 보여준 것과 다른 값이 저장되지 않는다.
  const parsedKeywords = parseKeywords(keywordText)

  return (
    <div>
      <Link className="back-link" to="/admin">
        ← 검수
      </Link>

      <div className="admin-bar">
        <div>
          <h1 className="page-title">시민 문제 확정</h1>
          <p className="page-desc">
            공개 화면에 나가는 <b>제목</b>·<b>요약</b>·<b>키워드</b>를 정합니다. 검수 상태는 이
            화면에서 바꾸지 않습니다 — 공개 전환은 검수 화면에서 합니다.
          </p>
        </div>
        {c !== undefined && (
          <div className="admin-who">
            <span className="muted small">검수 상태: {c.reviewStatusLabel}</span>
          </div>
        )}
      </div>

      {!mayWrite && (
        <div className="notice">
          읽기 권한만 있는 계정입니다. 저장하려면 <b>card.write</b> 권한이 있어야 합니다.
        </div>
      )}

      {actionError !== null && (
        <div className="notice error" role="alert">
          {actionError}
        </div>
      )}

      {save.isSuccess && (
        <div className="notice" role="status">
          저장했습니다.
        </div>
      )}

      <QueryState isPending={card.isPending} error={card.error}>
        {c !== undefined && (
          <>
            {c.titleIsDerived && (
              <div className="notice">
                <b>제목이 아직 원문에서 잘라 온 것입니다.</b> 이 상태로는 공개할 수 없습니다 —
                제목을 고쳐 저장하면 공개 전환이 열립니다.
              </div>
            )}

            <form
              className="panel"
              onSubmit={(e) => {
                e.preventDefault()
                if (canSave) save.mutate()
              }}
            >
              <div className="field">
                <label htmlFor="problem-title">
                  제목 <span className="req">*</span>
                </label>
                <p className="hint">
                  사람 이름·연락처·상세 주소 등 신원이 드러나는 말을 넣지 않습니다.
                </p>
                <input
                  id="problem-title"
                  type="text"
                  value={title}
                  maxLength={120}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="field">
                <label htmlFor="problem-summary">공개 요약</label>
                <p className="hint">
                  AI가 초안을 넣어 둡니다 — 고쳐서 저장하면 그 문장이 공개됩니다. 비우고
                  저장하면 <b>미확정</b>으로 돌아갑니다(공개 화면에는 요약이 안 나옵니다).
                </p>
                <textarea
                  id="problem-summary"
                  rows={4}
                  value={summary}
                  maxLength={SUMMARY_MAX}
                  onChange={(e) => setSummary(e.target.value)}
                />
                <div className="hint">
                  {summary.trim().length}/{SUMMARY_MAX}자
                  {c.summary === null && summary.trim() === '' && ' · 아직 요약이 없습니다'}
                </div>
              </div>

              <div className="field">
                <label htmlFor="problem-keywords">AI 추출 키워드</label>
                <p className="hint">
                  AI가 원문에서 뽑아 둡니다 — 쉼표로 구분해 고칩니다(최대 {KEYWORD_MAX}개).
                  공개 상세 화면에 그대로 나갑니다. 비우고 저장하면 AI 재실행이 다시 채웁니다.
                </p>
                <input
                  id="problem-keywords"
                  type="text"
                  value={keywordText}
                  onChange={(e) => setKeywordText(e.target.value)}
                  placeholder="이동지원, 장보기, 돌봄"
                />
                <div className="hint">
                  {parsedKeywords.length === 0
                    ? '아직 키워드가 없습니다'
                    : parsedKeywords.map((k) => `#${k}`).join(' ')}
                </div>
              </div>

              <button className="btn" type="submit" disabled={!canSave}>
                {save.isPending ? '저장 중…' : '저장'}
              </button>
            </form>

            <div className="panel">
              <h2 className="section-title">시민이 적은 내용</h2>
              {canReadRaw ? (
                <>
                  <p className="hint">
                    원문은 공개 화면 어디에도 나가지 않습니다. 이 열람은 기록에 남습니다.
                  </p>
                  <p style={{ whiteSpace: 'pre-wrap' }}>
                    {c.rawText ?? <span className="muted">원문이 없습니다.</span>}
                  </p>
                </>
              ) : (
                <p className="muted">
                  원문을 보려면 <b>pii.read</b> 권한이 필요합니다.
                </p>
              )}
              <p className="muted small">
                지역 {c.region.name} · 접수 {c.intakeChannel} ·{' '}
                {c.needsReview ? '확인 필요' : '확인 완료'}
              </p>
            </div>
          </>
        )}
      </QueryState>
    </div>
  )
}
